/* ─────────────────────────────────────────────────────────────
   LandingFooter — bottom-anchored slide-up drawer for the landing
   page. A bouncing chevron sits at the bottom-centre with the
   word MORE above it (both white on low opacity, no pill). Two
   triggers open the drawer: (a) click the chevron, (b) scroll the
   mouse-wheel downward while the landing page is visible. Scroll
   up closes it again. The drawer is a dark frosted panel covering
   ~28vh on desktop and the full viewport on mobile.

   Layout inside the drawer:
     Left  — F logo, wordmark, tagline, socials (icon-only, grey to
             white on hover).
     Right — three link columns (Explore / Support / Legal) pushed
             tight to the right edge for a Gorilla-style footer feel.

     Below both  — divider, then the Mojang-independence disclaimer,
             then a small copyright line at the very bottom.
   ───────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronUp, X } from "lucide-react";
import { pageTransitionTo } from "./PageTransition";
import { SOCIALS } from "./NewSiteConstants";

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const MOBILE_BP = 900;
const SCROLL_TRIGGER_DELTA = 32; // wheel delta needed to fire open/close

const EXPLORE = [
  { label: "Marketplace",          path: "/products/marketplace" },
  { label: "Pixel Sculpt",         path: "/studio?app=block-builder&tab=0" },
  { label: "Schematic Library",    path: "/studio?app=world-builder&tab=0&library=1" },
  { label: "Schematic Converter",  path: "/studio?app=asset-tools&tab=0" },
  { label: "Structure Generator",  path: "/studio?app=asset-tools&tab=0" },
  { label: "Pixelizer",            path: "/studio?app=asset-tools&tab=1" },
  { label: "Voxelizer",            path: "/studio?app=asset-tools&tab=1" },
  { label: "Maze Builder",         path: "/studio?app=map-maze-builder&tab=1" },
  { label: "Skin Editor",          path: "/skin-editor" },
];

const SUPPORT = [
  { label: "Help Centre",      path: "/help-center" },
  { label: "About Us",         path: "/about" },
  { label: "Contact",          path: "/contact" },
  { label: "Features",         path: "/features" },
  { label: "Latest News",      path: "/news" },
  { label: "Development Hub",  path: "/development-hub" },
];

const LEGAL = [
  { label: "Terms and Conditions", path: "/terms" },
  { label: "Privacy Policy",       path: "/privacy" },
  { label: "Cookie Policy",        path: "/cookie-policy" },
  { label: "Refund Policy",        path: "/refund-policy" },
  { label: "Texture Library",      path: "/products/texture-library" },
];

/* Column header — small, uppercase, muted purple accent. */
function ColHead({ children }) {
  return (
    <div style={{
      fontSize: "0.66rem", fontWeight: 700,
      color: "#c4b5fd", letterSpacing: "0.20em",
      textTransform: "uppercase", marginBottom: 12,
    }}>{children}</div>
  );
}

function FooterLink({ label, path, onNavigate }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      role="link" tabIndex={0}
      data-testid={`landing-footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
      onClick={() => onNavigate(path)}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNavigate(path); } }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        fontSize: "0.84rem",
        color: hover ? "#fff" : "rgba(255,255,255,0.66)",
        cursor: "pointer",
        padding: "4px 0",
        lineHeight: 1.45,
        transition: "color 0.22s ease, transform 0.22s ease",
        transform: hover ? "translateX(2px)" : "translateX(0)",
        outline: "none",
        userSelect: "none",
      }}
    >{label}</div>
  );
}

/* Social icon — no pill, no border. Just the glyph, grey to white
   on hover. Keeps the brand row visually calm. */
function SocialIcon({ Icon, url, label }) {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={url}
      target="_blank" rel="noopener noreferrer"
      aria-label={label}
      data-testid={`landing-footer-social-${label}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        color: hover ? "#fff" : "rgba(255,255,255,0.52)",
        transition: "color 0.25s ease, transform 0.25s ease",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        display: "inline-flex",
        padding: "6px 8px",
      }}
    >
      <Icon size={20} />
    </a>
  );
}

/* Trigger — MORE label above a bouncing chevron. Both white with
   low opacity, no pill background. Hover pauses the bounce and
   lifts the opacity. On mobile the trigger sits at the bottom-left
   so the burger menu stays at the top-left and the trigger doesn't
   fight it visually. */
function ChevronTrigger({ onClick, isMobile }) {
  const [hover, setHover] = useState(false);
  return createPortal(
    <div
      data-testid="landing-footer-trigger"
      role="button" tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "fixed",
        bottom: 18,
        ...(isMobile
          ? { left: 18, transform: "none" }
          : { left: "50%", transform: "translateX(-50%)" }),
        zIndex: 45,
        cursor: "pointer",
        fontFamily: FONT,
        display: "flex", flexDirection: "column",
        alignItems: "center", gap: 2,
        userSelect: "none",
        padding: "4px 12px",
      }}
    >
      <div style={{
        fontSize: "0.62rem", fontWeight: 700,
        color: hover ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)",
        letterSpacing: "0.24em", textTransform: "uppercase",
        transition: "color 0.25s ease",
      }}>More</div>
      <div style={{
        color: hover ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)",
        display: "inline-flex",
        transition: "color 0.25s ease",
        animation: hover ? "none" : "lfFooterBounce 2.2s ease-in-out infinite",
      }}>
        <ChevronUp size={22} strokeWidth={2.2} />
      </div>
      <style>{`
        @keyframes lfFooterBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
      `}</style>
    </div>,
    document.body
  );
}

export default function LandingFooter() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rendered, setRendered] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < MOBILE_BP
  );
  // Debounce so a single scroll gesture doesn't open + close in the
  // same tick. Also gates the wheel handler after we react.
  const wheelLockRef = useRef(0);
  // Mirror of `open` for observer callbacks so they never read a
  // stale value from useEffect's closure.
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    const on = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
      return () => cancelAnimationFrame(r);
    } else if (rendered) {
      setVisible(false);
      const t = setTimeout(() => setRendered(false), 700);
      return () => clearTimeout(t);
    }
  }, [open, rendered]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  /* True while the site menu or Flux drawer is open. Both are
     scrollable sheets that take over the viewport; we don't want a
     wheel gesture inside them to accidentally open our footer, and
     if the user opens either while our footer is already up we
     tuck the footer away so only one sheet is active at a time. */
  const isOverlayOpen = useCallback(() => {
    return !!(
      document.querySelector('[data-testid="menu-overlay"]') ||
      document.querySelector('[data-testid="flux-panel"]')
    );
  }, []);

  /* Close footer whenever menu or Flux opens on top of it. Uses a
     MutationObserver on document.body since both drawers portal in
     and out without a shared parent. Reads openRef so it stays
     accurate across re-registrations. */
  useEffect(() => {
    if (typeof window === "undefined" || !window.MutationObserver) return;
    const check = () => {
      if (openRef.current && isOverlayOpen()) setOpen(false);
    };
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    // Initial check in case an overlay was already open when we mounted
    check();
    return () => obs.disconnect();
  }, [isOverlayOpen]);

  /* Scroll-wheel activation.
       - Wheel DOWN (deltaY > 0) opens the drawer when closed, but
         only if (a) no other overlay (menu / Flux) is open, and
         (b) either the underlying page is not scrollable or the
         user has already scrolled to the very bottom of it.
       - Wheel UP   (deltaY < 0) closes the drawer when open, but
         only if the drawer's own scrollable body is already at its
         top (scrollTop === 0). This lets users scroll DOWN inside
         the drawer freely, then a further wheel-up gesture from the
         top closes it.
       - `wheelLockRef` blocks a second trigger for 500ms so the
         same gesture never toggles twice.
  */
  useEffect(() => {
    const onWheel = (e) => {
      const now = Date.now();
      if (now < wheelLockRef.current) return;
      if (Math.abs(e.deltaY) < SCROLL_TRIGGER_DELTA) return;

      if (e.deltaY > 0 && !open) {
        // Guard: don't hijack wheel while menu or Flux is open —
        // those are their own scrollable sheets.
        if (isOverlayOpen()) return;
        // Guard: if the underlying page can scroll and hasn't
        // reached the bottom yet, let the page scroll first.
        const doc = document.documentElement;
        const pageScrollable = doc.scrollHeight - window.innerHeight > 2;
        if (pageScrollable) {
          const atBottom = window.scrollY + window.innerHeight >= doc.scrollHeight - 2;
          if (!atBottom) return;
        }
        setOpen(true);
        wheelLockRef.current = now + 500;
        return;
      }
      if (e.deltaY < 0 && open) {
        // If the drawer body has scrolled down, absorb the wheel-up
        // as an internal scroll; only close once we're back at top.
        const scroller = document.querySelector('.lf-footer-scroll');
        if (scroller && scroller.scrollTop > 2) return;
        setOpen(false);
        wheelLockRef.current = now + 500;
      }
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, [open, isOverlayOpen]);

  const handleNavigate = useCallback((path) => {
    setOpen(false);
    setTimeout(() => pageTransitionTo(path, navigate), 200);
  }, [navigate]);

  const drawerH = isMobile ? "100vh" : "auto";
  const drawerMinH = isMobile ? "100vh" : 380;
  const drawerMaxH = isMobile ? "100vh" : "60vh";

  return (
    <>
      {!open && <ChevronTrigger onClick={() => setOpen(true)} isMobile={isMobile} />}

      {rendered && createPortal(
        <>
          <div
            data-testid="landing-footer-backdrop"
            onClick={() => setOpen(false)}
            style={{
              position: "fixed", inset: 0,
              zIndex: 60,
              background: "rgba(9,10,15,0.42)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? "auto" : "none",
              transition: "opacity 0.6s cubic-bezier(0.4,0,0.2,1)",
            }}
          />

          <aside
            data-testid="landing-footer-drawer"
            role="dialog" aria-label="Site footer"
            style={{
              position: "fixed", left: 0, right: 0, bottom: 0,
              height: drawerH, minHeight: drawerMinH, maxHeight: drawerMaxH,
              zIndex: 61,
              background: "rgba(14,14,20,0.96)",
              backdropFilter: "blur(20px) saturate(1.2)",
              WebkitBackdropFilter: "blur(20px) saturate(1.2)",
              borderTop: "1px solid rgba(255,255,255,0.08)",
              boxShadow: visible ? "0 -12px 60px rgba(0,0,0,0.45)" : "none",
              transform: visible ? "translateY(0)" : "translateY(100%)",
              opacity: visible ? 1 : 0,
              transition: "transform 0.7s cubic-bezier(0.4,0,0.2,1), opacity 0.7s cubic-bezier(0.4,0,0.2,1), box-shadow 0.7s ease",
              color: "#f4f1f7",
              fontFamily: FONT,
              overflow: "hidden",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Close pill top-right on mobile only. Desktop hides
                it because the wheel-up gesture already covers close. */}
            <button
              data-testid="landing-footer-close"
              onClick={() => setOpen(false)}
              aria-label="Close footer"
              style={{
                position: "absolute", top: 14, right: 20,
                width: 34, height: 34, borderRadius: 8,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "rgba(255,255,255,0.72)",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.25s ease, color 0.25s ease",
                zIndex: 3,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.14)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.72)"; }}
            ><X size={16} /></button>

            {/* Body — main row plus the divider-and-fine-print
                strip below it. A max-width wrapper keeps the drawer
                content visually centred so the left brand column and
                the right link columns have equal breathing room from
                the drawer edges (roughly a 10vw gutter each side on
                wide screens). */}
            <div className="lf-footer-scroll" style={{
              flex: 1, overflowY: "auto",
              padding: isMobile ? "28px 22px 20px" : "36px max(48px, 5vw) 20px",
              WebkitOverflowScrolling: "touch",
              display: "flex", flexDirection: "column",
            }}>
              <div style={{
                width: "100%",
                maxWidth: 1440,
                margin: "0 auto",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  // Left brand column stays flexible; right columns
                  // hug the far right with fixed widths so no empty
                  // space is wasted on desktop.
                  : "minmax(280px, 1fr) 190px 180px 210px",
                gap: isMobile ? 24 : 48,
                alignItems: "start",
                width: "100%",
              }}>
                {/* ─── Brand column ─── */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <img
                      src="/lumenforge-f-logo.webp"
                      alt="LumenForge"
                      style={{
                        width: 34, height: 34,
                        filter: "drop-shadow(0 4px 14px rgba(59,130,246,0.4))",
                      }}
                    />
                    <span style={{
                      fontSize: "1rem", fontWeight: 800,
                      letterSpacing: "-0.01em", color: "#fff",
                    }}>LumenForge-MC</span>
                  </div>
                  <div style={{
                    fontSize: "0.86rem", fontWeight: 700,
                    color: "#fff", lineHeight: 1.4, marginBottom: 6,
                  }}>Build. Cut. Ship.</div>
                  <p style={{
                    fontSize: "0.8rem", color: "rgba(255,255,255,0.62)",
                    lineHeight: 1.6, margin: "0 0 14px", maxWidth: 340,
                  }}>
                    The all-in-one Minecraft creation platform. Textures, blocks, worlds and mazes, one browser tab.
                  </p>
                  {/* Icon-only social row, no boxes */}
                  <div style={{ display: "flex", gap: 4, marginLeft: -8 }}>
                    {SOCIALS.map((s) => (
                      <SocialIcon key={s.label} Icon={s.Icon} url={s.url} label={s.label} />
                    ))}
                  </div>
                </div>

                {/* ─── Explore ─── */}
                <div>
                  <ColHead>Explore</ColHead>
                  {EXPLORE.map(l => (
                    <FooterLink key={l.label} label={l.label} path={l.path} onNavigate={handleNavigate} />
                  ))}
                </div>

                {/* ─── Support ─── */}
                <div>
                  <ColHead>Support</ColHead>
                  {SUPPORT.map(l => (
                    <FooterLink key={l.label} label={l.label} path={l.path} onNavigate={handleNavigate} />
                  ))}
                </div>

                {/* ─── Legal ─── */}
                <div>
                  <ColHead>Legal</ColHead>
                  {LEGAL.map(l => (
                    <FooterLink key={l.label} label={l.label} path={l.path} onNavigate={handleNavigate} />
                  ))}
                </div>
              </div>

              {/* Spacer to push the fine-print strip to the bottom
                  of the drawer whenever the content is shorter than
                  the drawer height. */}
              <div style={{ flex: 1, minHeight: 20 }} />

              {/* Divider + fine print. Mojang-independence disclaimer
                  sits directly under the divider (per Gorilla layout);
                  the copyright sits below that in a slightly muted
                  smaller line. Both centred. */}
              <div style={{
                borderTop: "1px solid rgba(255,255,255,0.10)",
                paddingTop: 16,
                textAlign: "center",
              }}>
                <p style={{
                  fontSize: "0.76rem", color: "rgba(255,255,255,0.55)",
                  lineHeight: 1.55, margin: "0 0 8px",
                  maxWidth: 780, marginLeft: "auto", marginRight: "auto",
                }}>
                  Not an official Minecraft product. Not approved by or associated with Mojang or Microsoft. LumenForge-MC is an independent platform run by Insid3 Th3 Cub3.
                </p>
                <p style={{
                  fontSize: "0.7rem", color: "rgba(255,255,255,0.38)",
                  margin: 0, letterSpacing: "0.02em",
                }}>
                  &copy; {new Date().getFullYear()} Insid3 Th3 Cub3. All rights reserved.
                </p>
              </div>
              </div>
            </div>

            <style>{`
              .lf-footer-scroll::-webkit-scrollbar { width: 6px; }
              .lf-footer-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 3px; }
              .lf-footer-scroll::-webkit-scrollbar-track { background: transparent; }
            `}</style>
          </aside>
        </>,
        document.body
      )}
    </>
  );
}
